import * as React from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { withStyles } from '@material-ui/core/styles';

import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';

import { IProductGridHandle, IProductGridProps } from './ProductGrid.properties';
import styles from './ProductGrid.styles';
import ImageView from 'components/common/ImageView';
import DataSource from 'devextreme/data/data_source';
import { ICatalogueProductFacade } from 'api/swaggerTypes';
import Paper from '@material-ui/core/Paper';
import { ARROW_DOWN, ARROW_UP, ENTER_KEY, PaperElevation, TAB } from 'utils/constants';
import { createDataSource, useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { useRetrieveCatalogueProducts } from 'api/Worksale/catalogue';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns } from './ProductGrid.constants';

const DEFAULT_ROW_HEIGHT = 44;

function ProductGrid(props: Readonly<IProductGridProps>, ref: React.Ref<IProductGridHandle>): JSX.Element {
    const {
        catalogueId, defaultSelectedId, worksaleId, loadImage,
        classes, getMainImage, getListOfImages, hideImage = false, allowArrowKeysNavigation = true,
        onProductSelectionChanged, showDetailOnClick, onKeyDown
    } = props;
    const { preventDataGridDefaults } = useGridKeyboardNavigation();
    const dataGridInnerRef = React.useRef<DataGrid>();
    const [selectedProduct, setSelectedProduct] = React.useState<ICatalogueProductFacade>();
    const [productDataSource, setProductDataSource] = React.useState<DataSource>(new DataSource([]));
    const [isFirstTimeLoad, setIsFirstTimeLoad] = React.useState<boolean>(true);
    const entryProductsFetchMutation = useRetrieveCatalogueProducts();

    const loadProducts = React.useCallback(
        async (BatchPage, BatchSize) => {
            if (Boolean(worksaleId) && Boolean(catalogueId)) {
                const response = await entryProductsFetchMutation.mutateAsync({
                    WorksaleId: worksaleId,
                    CatalogueId: catalogueId,
                    urlQueryParams: {
                        BatchPage,
                        BatchSize
                    }
                });

                return response?.EntryProducts ?? [];
            } else {
                return [];
            }
        },
        [worksaleId, catalogueId]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            reloadLines(): Promise<any> {
                return dataGridInnerRef.current?.instance.refresh();
            },
            selectDefaultRow(): void {
                if (defaultSelectedId) {
                    dataGridInnerRef.current?.instance.selectRows([defaultSelectedId], false);
                } else {
                    dataGridInnerRef.current?.instance.selectRowsByIndexes([0]);
                }

                const selectedElemet = document.querySelector('tr[aria-selected="true"]') as any;
                if (selectedElemet) dataGridInnerRef.current?.instance.focus(selectedElemet);

            },
            reload(): Promise<any> {
                return productDataSource?.reload();
            }
        })
    );

    React.useEffect(
        () => {
            if (catalogueId && worksaleId) {
                dataGridInnerRef.current?.instance?.clearSelection();
                setSelectedProduct(undefined);
                setIsFirstTimeLoad(true);
                setProductDataSource(
                    () =>
                        createDataSource(
                            'CatalogueProductId',
                            { fetch: loadProducts }
                        ));
            } else {
                setProductDataSource(null);
            }
        },
        [catalogueId, worksaleId]
    );

    const handleDataGridKeyDown = async (e) => {
        const keyboardEvent = e.event as KeyboardEvent;

        // MPF-1664 - Handle tabs navigation
        preventDataGridDefaults(e);

        if (onKeyDown) {
            onKeyDown(keyboardEvent);
        }

        if (keyboardEvent.keyCode === ENTER_KEY && selectedProduct) {
            showDetailOnClick({ data: selectedProduct });

            return;
        }
        const grid = dataGridInnerRef.current?.instance;
        if (allowArrowKeysNavigation) {
            const selKey = e.component.getSelectedRowKeys();
            const visibleRowsLength = e.component.getVisibleRows().length;
            const clientHeight = document.getElementsByClassName('dx-datagrid-rowsview')[0].clientHeight;

            if (selKey.length) {
                const currentKey = selKey[0];
                let index = e.component.getRowIndexByKey(currentKey);

                if (keyboardEvent.keyCode === ARROW_UP) {
                    index--;
                    if (index >= 0) {
                        await e.component.selectRowsByIndexes([index]);
                        if ((index * DEFAULT_ROW_HEIGHT) <= DEFAULT_ROW_HEIGHT) {
                            grid.getScrollable().scrollBy(-DEFAULT_ROW_HEIGHT);
                        }
                        keyboardEvent.stopPropagation();
                    }
                } else if (keyboardEvent.keyCode === ARROW_DOWN) {
                    index++;
                    if (index < visibleRowsLength) {
                        await e.component.selectRowsByIndexes([index]);
                        if ((index + 1) * DEFAULT_ROW_HEIGHT > clientHeight) {
                            grid.getScrollable().scrollBy(DEFAULT_ROW_HEIGHT);
                        }
                        keyboardEvent.stopPropagation();
                    }
                }
            }
            e.component.focus();
        }

        if (keyboardEvent.keyCode === TAB) {
            setTimeout(
                () => {
                    unFocusGrid();
                },
                0);
        }
    };

    const deselectRow = () => {
        dataGridInnerRef.current?.instance.deselectAll()
            .catch((err) => console.warn(err));
    };

    function unFocusGrid(): any {
        deselectRow();
        dataGridInnerRef.current?.instance.clearSelection();
        setSelectedProduct(undefined);
        if (onProductSelectionChanged) {
            onProductSelectionChanged(undefined);
        }
    }

    const focusCellChanging = React.useCallback(
        (e) => {
            e.isHighlighted = false;
        },
        [allowArrowKeysNavigation]);

    const selectionChanged = React.useCallback(
        (e) => {
            const selectedRowData = e?.selectedRowsData?.length > 0 ? e.selectedRowsData[0] : undefined;
            setSelectedProduct(selectedRowData);
            if (onProductSelectionChanged) {
                onProductSelectionChanged(selectedRowData);
            }
        },
        []);

    const onContentReady = React.useCallback(
        async () => {
            if (dataGridInnerRef.current?.instance?.getDataSource()?.items().length > 0 && isFirstTimeLoad) {

                if (defaultSelectedId) {
                    await dataGridInnerRef.current?.instance.selectRows([defaultSelectedId], false);
                    const productData = await dataGridInnerRef.current?.instance.getSelectedRowsData()?.[0];
                    onProductSelectionChanged(productData);
                } else {
                    dataGridInnerRef.current.instance.selectRowsByIndexes([0])
                        .catch((err) => console.warn(err));
                }

                setIsFirstTimeLoad(false);
            }
        },
        [isFirstTimeLoad, defaultSelectedId, dataGridInnerRef.current]
    );

    return (
        <div className={classes.parentContainer} >
            <Paper className={classes.productGrid}>
                <DynamicDataGrid
                    standardKeyboardNavigation={false}
                    dataGridClassName={classes.productDataGrid}
                    columns={columns}
                    allowHeaderFilting={false}
                    allowUpdating={false}
                    allowDelete={false}
                    actionButtonsVisibility={false}
                    selection='single'
                    gridProps={{
                        dataSource: productDataSource,
                        dataGridRef: dataGridInnerRef,
                        onContentReady: onContentReady,
                        onSelectionChanged: selectionChanged,
                        onFocusedCellChanging: focusCellChanging,
                        onKeyDown: handleDataGridKeyDown,
                        allowColumnResizing: true,
                        keyExpr: 'Code'
                    }}
                    detailOnClick={showDetailOnClick}
                />
            </Paper>
            {!hideImage &&
                <>
                    <Paper elevation={PaperElevation} className={classes.productImageDiv}>
                        <ImageView
                            dialogTitle='Product Image'
                            getMainImage={getMainImage}
                            mainImageObject={selectedProduct?.MainImage?.[0] ?? null}
                            loadImage={loadImage}
                            imageCount={selectedProduct?.ImageCount ?? 0}
                            getListOfImages={() => getListOfImages(selectedProduct?.CatalogueProductId)}
                        />
                    </Paper>
                </>
            }
        </div>
    );
}

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(ProductGrid)));
